import { useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDown, Copy } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import type { GetQuotesResponse } from "@pbz/schema/lead";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Loader,
} from "@pbz/ui-kit";

import { FormLabel } from "@/components/FormLabel";
import { useGetQuotes, useSaveUserSelection } from "@/queries/lead";
import { type PlanSelectionForm, planSelectionSchema } from "@/schema/lead";

type Quote = GetQuotesResponse[0];

interface SelectPlanProps {
  leadId: number;
  arn: string;
  defaultValues?: PlanSelectionForm;
  onSuccess: () => void;
}

const SelectPlan: React.FC<SelectPlanProps> = ({
  leadId,
  arn = "",
  defaultValues,
  onSuccess,
}) => {
  const [generatedUrl, setGeneratedUrl] = useState<string>("");
  const [isUrlCopied, setIsUrlCopied] = useState(false);
  const hasARN = !!arn;

  const form = useForm<PlanSelectionForm>({
    resolver: zodResolver(planSelectionSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      bankId: -1,
      planId: -1,
      ...(defaultValues || {}),
    },
    disabled: hasARN,
  });

  const selectedBankId = form.watch("bankId");
  const selectedPlanId = form.watch("planId");

  // Get Quotes (Plans) - only fetch when bank is selected
  const { isLoading: isLoadingPlans, data: plansData } = useGetQuotes(leadId);
  const plans = plansData?.data || [];

  // Group plans by providerId to create banks options list
  const bankWithPlans = useMemo(() => {
    const groupedPlans = plans.reduce(
      (
        acc: Record<number, { id: number; name: string; plans: Quote[] }>,
        plan,
      ) => {
        const providerId = plan.providerId;
        if (!acc[providerId]) {
          acc[providerId] = {
            id: providerId,
            name: plan.providerName,
            plans: [],
          };
        }
        acc[providerId].plans.push(plan);
        return acc;
      },
      {},
    );

    return Object.values(groupedPlans);
  }, [plans]);

  // Save User Selection mutation
  const saveUserSelectionMutation = useSaveUserSelection({
    onSuccess: (data) => {
      if (data?.next?.config?.bankUrl || data?.next?.config?.url) {
        const url = data.next.config.bankUrl || data.next.config.url || "";
        setGeneratedUrl(url);
        toast.success("Plan selection saved successfully");
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(
        error.message || "Failed to save plan selection. Please try again.",
      );
    },
  });
  const { isPending: isSubmitting } = saveUserSelectionMutation;

  const handleFormSubmit = (data: PlanSelectionForm) => {
    if (hasARN) {
      return;
    }

    // Find the selected plan to get the required data
    const selectedPlan = plans.find(
      (plan: Quote) => plan.planId === data.planId,
    );
    if (!selectedPlan) {
      toast.error("Selected plan not found");
      return;
    }

    saveUserSelectionMutation.mutate({
      leadId,
      selection: {
        supplierId: selectedPlan.providerId,
        planId: selectedPlan.planId,
        redirectionUrl: selectedPlan.selection?.redirectionUrl || "",
        paymentFrequencyId: selectedPlan.paymentFrequency.id,
        profileId: selectedPlan.selection?.profileId || 0,
        iosRedirection: selectedPlan.selection?.iosRedirection || "",
        androidRedirection: selectedPlan.selection?.androidRedirection || "",
      },
    });
  };

  const getSelectedBank = (fieldValue: number) => {
    const selectedBank = bankWithPlans.find((bank) => bank.id === fieldValue);
    return selectedBank ? (
      selectedBank.name
    ) : (
      <span className="text-gray-500">Please Select Bank</span>
    );
  };

  const getSelectedPlan = (fieldValue: number) => {
    const selectedPlan = plans.find(
      (plan: Quote) => plan.planId === fieldValue,
    );
    return selectedPlan ? (
      selectedPlan.planName
    ) : (
      <span className="text-gray-500">Please Select Plan</span>
    );
  };

  const handleCopyUrl = async () => {
    if (generatedUrl) {
      try {
        await navigator.clipboard.writeText(generatedUrl);
        setIsUrlCopied(true);
        toast.success("URL copied to clipboard");
        setTimeout(() => setIsUrlCopied(false), 2000);
      } catch {
        toast.error("Failed to copy URL");
      }
    }
  };

  const handleBankChange = (bankId: number) => {
    form.setValue("bankId", bankId);
    // Reset plan selection when bank changes
    form.resetField("planId");
    setGeneratedUrl("");
  };

  return (
    <Card className="mb-10 bg-white">
      <CardHeader>
        <CardTitle>Plan Selection</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bank Selection */}
              <FormField
                control={form.control}
                name="bankId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Bank</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingPlans || hasARN}
                            variant="outline"
                          >
                            {getSelectedBank(Number(field.value))}
                            <div className="flex items-center justify-center gap-2">
                              {isLoadingPlans && <Loader className="h-4 w-4" />}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => handleBankChange(-1)}
                          >
                            <span className="text-gray-500">
                              Please Select Bank
                            </span>
                          </DropdownMenuItem>
                          {bankWithPlans.map(
                            ({ id: bankId, name: bankName }) => (
                              <DropdownMenuItem
                                className="cursor-pointer"
                                key={bankId}
                                onClick={() => handleBankChange(bankId)}
                              >
                                {bankName}
                              </DropdownMenuItem>
                            ),
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Plan Selection - Only visible when bank is selected */}
              {selectedBankId > -1 && (
                <FormField
                  control={form.control}
                  name="planId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Plan</FormLabel>
                      <FormControl>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              className="w-full justify-between"
                              disabled={isLoadingPlans || hasARN}
                              variant="outline"
                            >
                              {getSelectedPlan(Number(field.value))}
                              <div className="flex items-center justify-center gap-2">
                                {isLoadingPlans && (
                                  <Loader className="h-4 w-4" />
                                )}
                                <ChevronDown className="h-4 w-4 opacity-50" />
                              </div>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => field.onChange(-1)}
                            >
                              <span className="text-gray-500">
                                Please Select Plan
                              </span>
                            </DropdownMenuItem>
                            {bankWithPlans
                              .find(
                                (bankPlan) => bankPlan.id === selectedBankId,
                              )
                              ?.plans.map((plan: Quote) => (
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  key={plan.planId}
                                  onClick={() => field.onChange(plan.planId)}
                                >
                                  {plan.planName}
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Copy URL Button - Only visible when URL is generated */}
              {generatedUrl && (
                <div className="flex items-end">
                  <Button
                    className="w-full"
                    onClick={handleCopyUrl}
                    type="button"
                    variant="outline"
                  >
                    Copy URL
                    {isUrlCopied ? (
                      <Check className="ml-2 h-4 w-4" />
                    ) : (
                      <Copy className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Update Button */}
            <div className="flex justify-end">
              <Button
                className="bg-blue-500 text-white hover:bg-blue-600"
                disabled={isSubmitting || selectedPlanId === -1 || hasARN}
                type="submit"
              >
                {isSubmitting ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default SelectPlan;
